import { AxiosError } from "axios";
import { getStaleNonceMessage, isStaleNonceError } from "./txError";
import { GenericErrorResponse } from "../codegen-api";
import {
  CONTRACT_REVERT_DATA_RE,
  ERROR_SIGNATURE_BY_SELECTOR,
  KNOWN_REVERT_SELECTORS,
  SELECTOR_RE,
} from "./errors.constants";
import { extractErrorDataCandidates } from "./errors.extract";
import {
  decodeRawContractError,
  formatSelectorOnlyContractError,
} from "./errors.format";
import { formatParsedContractError } from "./errors.decode";

export const unwrapApiError = (error: any) => {
  const e = error as AxiosError<GenericErrorResponse>;
  const message = e.response?.data.error || (e as Error).message;
  return message;
};

export const hasContractRevertData = (value: unknown) =>
  typeof value === "string" && CONTRACT_REVERT_DATA_RE.test(value);

export const formatPotentialContractError = (value: unknown) => {
  if (!hasContractRevertData(value)) return undefined;
  return formatContractError(value);
};

/**
 * Extract a short, user-friendly message from an ethers/contract error.
 * Falls back to error.reason, error.shortMessage, error.message (first line),
 * trimmed to ~200 chars so it fits inside a modal.
 */
// The request CALLDATA (e.g. the openPosition input) leaks into the error object
// (err.transaction.data) and its stringified message. It is NOT a revert reason —
// its leading 4 bytes are a FUNCTION selector — but it is shaped exactly like
// custom-error data, so the raw decoder would otherwise display it as the revert
// (the misleading "Error code: 0x51359a95" / openPosition selector). Collect it so
// we can drop it from the revert-data candidates.
const collectRequestCallData = (err: any, depth = 0): string[] => {
  if (!err || typeof err !== "object" || depth > 4) return [];
  const out: string[] = [];
  const push = (v: unknown) => {
    if (typeof v === "string" && /^0x[0-9a-fA-F]{8}/.test(v)) out.push(v.toLowerCase());
  };
  push(err.transaction?.data);
  push(err.tx?.data);
  push(err.info?.payload?.params?.[0]?.data);
  push(err.requestBody && (() => { try { return JSON.parse(err.requestBody)?.params?.[0]?.data; } catch { return undefined; } })());
  return [
    ...out,
    ...collectRequestCallData(err.error, depth + 1),
    ...collectRequestCallData(err.info?.error, depth + 1),
  ];
};

/**
 * Remove ethers' trailing metadata without eating the sentence in front of it.
 *
 * This used to be `raw.replace(/\s+\(.*$/, "")` — delete everything from the
 * first " (" onward. That does strip ethers noise, but it cannot tell noise from
 * prose, and every path in the app funnels through here. What users actually
 * lost was the half of the message that helps:
 *
 *   "…lot size (0.0001). Increase your size."        -> "…lot size"
 *   "…on Monad Testnet (10143). Switch networks…"    -> "…on Monad Testnet"
 *   "…still settling (about one block). Try again…"  -> "…still settling"
 *   "…rejected the nonce request (HTTP 500): <why>"  -> "…the nonce request"
 *
 * Every one of those deletions removed the number the user needed or the action
 * they were meant to take. So match what ethers actually appends instead: a
 * trailing parenthetical of `key=value` pairs, and its docs link. Anything else
 * in brackets was written for a human and is kept.
 */
const ETHERS_SEE_LINK = /\s*\[\s*See:\s*https?:\/\/\S+?\s*\]/gi;
// A trailing "(...)" whose contents look like ethers metadata: at least one
// `key=` pair, no sentence punctuation before it closes the string.
const ETHERS_META_TAIL = /\s*\((?:[^()]*?\b[a-zA-Z]+=[^()]*)\)\s*$/;

export const stripEthersNoise = (message: string): string => {
  let out = message.replace(ETHERS_SEE_LINK, "");
  // Ethers can nest these, e.g. `(error={...}, code=X, version=Y)`; strip
  // repeatedly, but only while the tail still looks like metadata.
  let previous: string;
  do {
    previous = out;
    out = out.replace(ETHERS_META_TAIL, "");
  } while (out !== previous);
  return out.replace(/[;,]\s*$/, "").trim();
};

export const formatContractError = (err: any): string => {
  if (!err) return "";

  // Checked first, and deliberately here rather than in each order path.
  //
  // A stale wallet nonce is not a contract revert — there is no returndata to
  // decode — so every branch below falls through to the raw RPC sentence
  // ("Nonce provided for the transaction is lower than the current nonce of the
  // account"), which tells the trader neither what happened nor what to do.
  // This is the one formatter that spot, perp, deposits and approvals all funnel
  // through, so naming it once covers the paths that have no catch of their own
  // (useSpotOrder.place, for instance, lets the error propagate untouched).
  if (isStaleNonceError(err)) return getStaleNonceMessage();

  const callData = new Set(collectRequestCallData(err));
  const dataCandidates = extractErrorDataCandidates(err).filter(
    (d) => !callData.has(typeof d === "string" ? d.toLowerCase() : d)
  );
  const parsedMessage = dataCandidates
    .map((data) => formatParsedContractError(data))
    .find(Boolean);
  if (parsedMessage) return parsedMessage;

  // Custom-error data we couldn't decode via a signature: show the error code +
  // decoded args instead of leaking the raw byte string into the dialog.
  const decodedRaw = dataCandidates
    .map((data) => decodeRawContractError(data))
    .find(Boolean);
  if (decodedRaw) return decodedRaw;

  const candidates: (string | undefined)[] = [
    err?.errorName &&
      (err?.errorArgs
        ? `${err.errorName}(${err.errorArgs.join(", ")})`
        : err.errorName),
    err?.reason,
    err?.shortMessage,
    err?.error?.reason,
    err?.error?.message,
    typeof err?.data === "string" ? err.data : undefined,
    err?.data?.message,
    typeof err === "string" ? err : undefined,
    err?.message,
  ];

  let raw = candidates.find((c) => typeof c === "string" && c.length > 0) || "";

  raw = stripEthersNoise(raw).split("\n")[0].trim();

  const sel = raw.match(SELECTOR_RE)?.[1]?.toLowerCase();
  if (sel && (KNOWN_REVERT_SELECTORS[sel] || ERROR_SIGNATURE_BY_SELECTOR[sel])) {
    const rawData = dataCandidates.find((data) => data.toLowerCase().startsWith(sel));
    const selectorMessage = formatSelectorOnlyContractError(sel, rawData);
    if (selectorMessage) return selectorMessage;
  }

  if (raw.length > 200) raw = `${raw.slice(0, 197)}...`;
  return raw || "Transaction failed";
};
